// src/lib/api/client.ts
import { QueryClient } from "@tanstack/react-query";
import axios, { type AxiosRequestHeaders } from "axios";

import { reactQueryConfig } from "@configs";
import { useUiStore } from "@stores/uiStore";
import { useUserStore } from "@stores/userStore";

/** Web fallback – only used outside Electron */
const STATIC_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

/** Shared Axios instance */
const axiosClient = axios.create({
  baseURL: STATIC_BASE_URL ?? undefined,
});

/** --- runtime bootstrap: set local server if preload supplied it --- */
try {
  const win = globalThis.window;
  const isElectron = win?.env?.isElectron === true;

  if (isElectron) {
    axiosClient.defaults.baseURL = undefined;
    // synchronous preload field populated by preload script
    const syncLocal = win.env?.localServerUrl ?? null;

    if (syncLocal) {
      // override axios baseURL immediately so the first request uses local backend
      axiosClient.defaults.baseURL = syncLocal;
    } else {
      // Optionally try sync IPC method (exposed in preload) if available:
      const syncGetter = win.api?.getLocalServerUrlSync;

      if (typeof syncGetter === "function") {
        try {
          const url = syncGetter();

          if (url) axiosClient.defaults.baseURL = url;
        } catch {
          // ignore
        }
      }
    }
  }
} catch {
  // ignore errors while detecting electron/env
}

/** Cache for runtime backend resolution (keeps the same promise around) */
let backendUrlPromise: Promise<string | null> | null = null;
let salesSessionIdentity = "";
let salesSessionPromise: Promise<void> | undefined;

/** Requests and the React lifecycle share one initialization barrier. */
export function ensureElectronSalesSession(): Promise<void> {
  const desktopApi = globalThis.window?.api;

  if (!globalThis.window?.env?.isElectron || !desktopApi?.setSalesSession)
    return Promise.resolve();
  const { token, storeId } = useUserStore.getState();
  const language = useUiStore.getState().locale;
  const identity = JSON.stringify([token, storeId, language]);

  if (identity !== salesSessionIdentity) {
    salesSessionIdentity = identity;
    salesSessionPromise = desktopApi
      .setSalesSession(
        token && storeId ? { token, storeId: String(storeId), language } : null,
      )
      .catch((error: unknown) => {
        // A failed older initialization must not invalidate a newer account.
        if (salesSessionIdentity === identity) salesSessionIdentity = "";
        // Do not send a sale through the gateway without its session barrier.
        throw error;
      });
  }
  return salesSessionPromise ?? Promise.resolve();
}

/** Ensures async runtime base URL if needed (keeps backward compatibility) */
async function ensureRuntimeBaseUrlIfNeeded(): Promise<string | null> {
  const win = globalThis.window;
  const isElectron = win?.env?.isElectron === true;

  if (!isElectron) {
    return axiosClient.defaults.baseURL ?? null;
  }

  // Electron requests must pass through its local gateway, including online sales.
  if (axiosClient.defaults.baseURL) return axiosClient.defaults.baseURL;

  // reuse inflight promise
  if (backendUrlPromise) return backendUrlPromise;

  const getLocalServerUrl = win.api?.getLocalServerUrl;

  if (typeof getLocalServerUrl !== "function") return null;

  backendUrlPromise = getLocalServerUrl()
    .then((url: string | null) => {
      if (url) {
        axiosClient.defaults.baseURL = url;
        return url;
      }
      backendUrlPromise = null;
      return null;
    })
    .catch(() => {
      backendUrlPromise = null;
      return null;
    });

  return backendUrlPromise;
}

/** Interceptor: ensure runtime baseURL if electron and attach headers */
axiosClient.interceptors.request.use(
  async (config) => {
    const url = config.url ?? "";
    const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(url);

    // Only resolve baseURL if the request is relative
    if (!isAbsolute) {
      const runtimeUrl = await ensureRuntimeBaseUrlIfNeeded();

      if (globalThis.window?.env?.isElectron && !runtimeUrl)
        throw new axios.CanceledError("Local service is not ready");

      if (globalThis.window?.env?.isElectron && runtimeUrl)
        // Each interceptor invocation owns this request configuration.
        // eslint-disable-next-line require-atomic-updates
        config.baseURL = runtimeUrl;
      await ensureElectronSalesSession();

      config.headers = (config.headers as AxiosRequestHeaders) || {};

      if (
        globalThis.window?.env?.isElectron &&
        ["post", "delete"].includes(config.method ?? "") &&
        !config.headers.has("Idempotency-Key")
      ) {
        config.headers["Idempotency-Key"] = crypto.randomUUID();
      }

      try {
        const state = useUserStore.getState();
        const language = useUiStore.getState().locale;

        if (state?.token)
          config.headers.Authorization = `Bearer ${state.token}`;
        if (language) config.headers["X-Accept-Language"] = language;
      } catch {
        // ignore
      }
    }

    return config;
  },
  (error: Error) => Promise.reject(error),
);

const queryClient = new QueryClient(reactQueryConfig);

export { axiosClient, queryClient };
